import {ManageExecutionDistributionRule} from '@store/operations/executions';
import {useTranslation} from 'react-i18next';
import {Divider, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {AddWhite, Delete} from '@icons/index';
import {Sizes} from '@constants/sizes.constants';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import {map} from 'rxjs';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import React from 'react';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';

interface ExecutionDistributionRulesProps {
  errors: { [key: string]: any };
  distributionRules?: ManageExecutionDistributionRule[];
  onChange: (distributionRules: ManageExecutionDistributionRule[]) => void;
  organisationId: number;
  color?: 'primary' | 'secondary';
}

export const ExecutionDistributionRules = (props: ExecutionDistributionRulesProps) => {
  const {errors, distributionRules, onChange, organisationId, color = 'secondary'} = props;

  const {t} = useTranslation();

  const updateDistributionRule = (index: number, data: ManageExecutionDistributionRule) => {
    const dRules = [...(distributionRules || [])];
    dRules[index] = {...dRules[index], ...data};
    onChange(dRules);
  };

  return (<>
    <Stack py="20px" spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontSize={16} fontWeight="700">
          {t('operations.executions.manage.distributionRules.title')}
        </Typography>
        <IconButton
          aria-label="small-square-text"
          centerRipple={false}
          onClick={() => onChange((distributionRules || []).concat({}))}>
          <AddWhite/>
        </IconButton>
      </Stack>
      <Stack spacing={3}>
        {distributionRules?.map((dr, index) => (
          <Stack key={`split ${index}`}>
            <Stack flex={1} direction="row" alignItems="center" justifyContent="space-between" spacing={2} mr="-9px">
              <InputLabel sx={{width: 85}}>
                {t('operations.executions.manage.drNumber', {number: index + 1})}
              </InputLabel>
              <IconButton
                onClick={() => onChange((distributionRules || []).filter((i, iIndex) => iIndex !== index))}>
                <Delete/>
              </IconButton>
            </Stack>
            <Stack spacing={2}>
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('operations.executions.manage.distributionRules.unit')}
                placeholder={t('global.selectOption')}
                color={color}
                handleChange={(unit) => updateDistributionRule(index, {
                  clientOrganisationUnit: unit,
                  clientOrganisationUnitId: unit.value
                })}
                value={dr.clientOrganisationUnit}
                getOptions={(search) => filtersService.getFilters(TableFilterType.CLIENT_ORGANISATION_UNIT, FilterBaseEntityEnum.NONE, [{
                  type: TableFilterType.CLIENT_ORGANISATION,
                  values: [{value: organisationId, label: ''}]
                }], search).pipe(
                  map((options) => options.map((o) => ({label: o.name, value: o.id})))
                )}
                error={errors.distributionRules?.[index].unit}
                required/>
              <NumberTextFieldComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('operations.executions.manage.distributionRules.rateInPercent')}
                placeholder={t('global.writeSomething')}
                pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
                color={color}
                handleChange={(rateInPercent) => updateDistributionRule(index, {rateInPercent})}
                endAdornment={<Typography fontSize={14} fontWeight="400">%</Typography>}
                value={dr.rateInPercent}
                error={errors.distributionRules?.[index].rateInPercent}
                required/>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
    <Divider variant="fullWidth" orientation="horizontal"/>
  </>);

};

export default ExecutionDistributionRules;
